export interface Borrower {
  id: string
  name: string
  phone: string
  email: string
  address: string
  loanNumber: string
  fundedDate: string
  loanType: 'Conventional' | 'FHA' | 'VA' | 'Jumbo'
  interestRate: number
  loanAmount: number
  loa: string
  manager: string
  status: 'New' | 'Attempted' | 'Connected' | 'Converted'
  lastActivity: string
  engagementHistory: EngagementEntry[]
}

export interface EngagementEntry {
  id: string
  type: 'call' | 'note' | 'status' | 'followup'
  title: string
  timestamp: string
  body?: string
}

export const mockBorrowers: Borrower[] = [
  {
    id: '1',
    name: 'Sarah Mitchell',
    phone: '(555) 234-5678',
    email: 'sarah.mitchell@email.com',
    address: '1247 Oak Street, Portland, OR 97214',
    loanNumber: 'ML-2023-8847',
    fundedDate: '2023-08-15',
    loanType: 'Conventional',
    interestRate: 6.25,
    loanAmount: 485000,
    loa: 'Jennifer Adams',
    manager: 'Michael Chen',
    status: 'New',
    lastActivity: '2024-05-20',
    engagementHistory: [
      {
        id: 'e1',
        type: 'status',
        title: 'Added to engagement queue',
        timestamp: '2024-05-20 09:15 AM',
      },
    ],
  },
  {
    id: '2',
    name: 'Robert Thompson',
    phone: '(555) 876-5432',
    email: 'r.thompson@email.com',
    address: '892 Maple Avenue, Seattle, WA 98102',
    loanNumber: 'ML-2022-6634',
    fundedDate: '2022-11-03',
    loanType: 'FHA',
    interestRate: 5.875,
    loanAmount: 325000,
    loa: 'David Park',
    manager: 'Michael Chen',
    status: 'Attempted',
    lastActivity: '2024-05-28',
    engagementHistory: [
      {
        id: 'e2',
        type: 'call',
        title: 'Outbound call attempt',
        timestamp: '2024-05-28 02:30 PM',
        body: 'No answer, left voicemail',
      },
      {
        id: 'e3',
        type: 'status',
        title: 'Status changed to Attempted',
        timestamp: '2024-05-28 02:31 PM',
      },
    ],
  },
  {
    id: '3',
    name: 'Maria Rodriguez',
    phone: '(555) 345-6789',
    email: 'maria.r@email.com',
    address: '2156 Pine Road, Austin, TX 78704',
    loanNumber: 'ML-2021-4521',
    fundedDate: '2021-06-22',
    loanType: 'VA',
    interestRate: 5.5,
    loanAmount: 410000,
    loa: 'Jennifer Adams',
    manager: 'Sarah Williams',
    status: 'Connected',
    lastActivity: '2024-05-29',
    engagementHistory: [
      {
        id: 'e4',
        type: 'call',
        title: 'Successful connection',
        timestamp: '2024-05-29 10:15 AM',
        body: 'Spoke with borrower, interested in refinancing. Will follow up next week.',
      },
      {
        id: 'e5',
        type: 'status',
        title: 'Status changed to Connected',
        timestamp: '2024-05-29 10:20 AM',
      },
      {
        id: 'e6',
        type: 'followup',
        title: 'Follow-up scheduled',
        timestamp: '2024-05-29 10:21 AM',
        body: 'Scheduled for June 5th',
      },
    ],
  },
  {
    id: '4',
    name: 'James Wilson',
    phone: '(555) 567-8901',
    email: 'jwilson@email.com',
    address: '3421 Elm Street, Denver, CO 80202',
    loanNumber: 'ML-2023-9912',
    fundedDate: '2023-12-08',
    loanType: 'Jumbo',
    interestRate: 7.125,
    loanAmount: 825000,
    loa: 'Robert Kim',
    manager: 'Sarah Williams',
    status: 'New',
    lastActivity: '2024-05-22',
    engagementHistory: [
      {
        id: 'e7',
        type: 'status',
        title: 'Added to engagement queue',
        timestamp: '2024-05-22 08:00 AM',
      },
    ],
  },
  {
    id: '5',
    name: 'Emily Chen',
    phone: '(555) 789-0123',
    email: 'emily.chen@email.com',
    address: '567 Birch Lane, San Diego, CA 92101',
    loanNumber: 'ML-2020-3345',
    fundedDate: '2020-09-14',
    loanType: 'Conventional',
    interestRate: 6.75,
    loanAmount: 550000,
    loa: 'David Park',
    manager: 'Michael Chen',
    status: 'Converted',
    lastActivity: '2024-05-27',
    engagementHistory: [
      {
        id: 'e8',
        type: 'call',
        title: 'Initial contact',
        timestamp: '2024-05-25 11:00 AM',
        body: 'Discussed refinance options',
      },
      {
        id: 'e9',
        type: 'call',
        title: 'Follow-up call',
        timestamp: '2024-05-27 03:15 PM',
        body: 'Borrower ready to proceed with new loan',
      },
      {
        id: 'e10',
        type: 'status',
        title: 'Status changed to Converted',
        timestamp: '2024-05-27 03:20 PM',
      },
      {
        id: 'e11',
        type: 'note',
        title: 'Loan application started',
        timestamp: '2024-05-27 03:25 PM',
        body: 'New loan number: ML-2024-1156',
      },
    ],
  },
  {
    id: '6',
    name: 'David Martinez',
    phone: '(555) 901-2345',
    email: 'd.martinez@email.com',
    address: '789 Cedar Drive, Phoenix, AZ 85001',
    loanNumber: 'ML-2022-7788',
    fundedDate: '2022-04-19',
    loanType: 'FHA',
    interestRate: 6.0,
    loanAmount: 295000,
    loa: 'Jennifer Adams',
    manager: 'Sarah Williams',
    status: 'Attempted',
    lastActivity: '2024-05-29',
    engagementHistory: [
      {
        id: 'e12',
        type: 'call',
        title: 'Outbound call attempt',
        timestamp: '2024-05-29 09:45 AM',
        body: 'Busy signal',
      },
    ],
  },
  {
    id: '7',
    name: 'Lisa Anderson',
    phone: '(555) 012-3456',
    email: 'lisa.anderson@email.com',
    address: '1523 Willow Court, Boston, MA 02101',
    loanNumber: 'ML-2023-5566',
    fundedDate: '2023-03-27',
    loanType: 'Conventional',
    interestRate: 6.5,
    loanAmount: 675000,
    loa: 'Robert Kim',
    manager: 'Michael Chen',
    status: 'Connected',
    lastActivity: '2024-05-28',
    engagementHistory: [
      {
        id: 'e13',
        type: 'call',
        title: 'Successful connection',
        timestamp: '2024-05-28 01:30 PM',
        body: 'Borrower not interested at this time. Will check back in 6 months.',
      },
      {
        id: 'e14',
        type: 'status',
        title: 'Status changed to Connected',
        timestamp: '2024-05-28 01:35 PM',
      },
    ],
  },
  {
    id: '8',
    name: 'Michael Brown',
    phone: '(555) 123-4567',
    email: 'mbrown@email.com',
    address: '2890 Spruce Street, Miami, FL 33101',
    loanNumber: 'ML-2021-9923',
    fundedDate: '2021-10-11',
    loanType: 'VA',
    interestRate: 5.75,
    loanAmount: 380000,
    loa: 'David Park',
    manager: 'Sarah Williams',
    status: 'New',
    lastActivity: '2024-05-21',
    engagementHistory: [
      {
        id: 'e15',
        type: 'status',
        title: 'Added to engagement queue',
        timestamp: '2024-05-21 07:30 AM',
      },
    ],
  },
  {
    id: '9',
    name: 'Jennifer Taylor',
    phone: '(555) 234-5670',
    email: 'jtaylor@email.com',
    address: '4567 Ash Boulevard, Chicago, IL 60601',
    loanNumber: 'ML-2023-4433',
    fundedDate: '2023-07-05',
    loanType: 'Jumbo',
    interestRate: 7.25,
    loanAmount: 950000,
    loa: 'Jennifer Adams',
    manager: 'Michael Chen',
    status: 'Attempted',
    lastActivity: '2024-05-29',
    engagementHistory: [
      {
        id: 'e16',
        type: 'call',
        title: 'Outbound call attempt',
        timestamp: '2024-05-29 11:00 AM',
        body: 'Left voicemail with callback number',
      },
    ],
  },
  {
    id: '10',
    name: 'Christopher Lee',
    phone: '(555) 345-6781',
    email: 'c.lee@email.com',
    address: '6789 Poplar Avenue, Nashville, TN 37201',
    loanNumber: 'ML-2022-8899',
    fundedDate: '2022-08-30',
    loanType: 'Conventional',
    interestRate: 6.125,
    loanAmount: 445000,
    loa: 'Robert Kim',
    manager: 'Sarah Williams',
    status: 'New',
    lastActivity: '2024-05-23',
    engagementHistory: [
      {
        id: 'e17',
        type: 'status',
        title: 'Added to engagement queue',
        timestamp: '2024-05-23 10:00 AM',
      },
    ],
  },
  {
    id: '11',
    name: 'Amanda White',
    phone: '(555) 456-7892',
    email: 'awhite@email.com',
    address: '8901 Hickory Lane, Atlanta, GA 30301',
    loanNumber: 'ML-2020-2234',
    fundedDate: '2020-12-15',
    loanType: 'FHA',
    interestRate: 5.625,
    loanAmount: 310000,
    loa: 'David Park',
    manager: 'Michael Chen',
    status: 'Connected',
    lastActivity: '2024-05-29',
    engagementHistory: [
      {
        id: 'e18',
        type: 'call',
        title: 'Successful connection',
        timestamp: '2024-05-29 02:00 PM',
        body: 'Discussed current rates and refinance benefits. Borrower will think about it.',
      },
      {
        id: 'e19',
        type: 'status',
        title: 'Status changed to Connected',
        timestamp: '2024-05-29 02:10 PM',
      },
    ],
  },
  {
    id: '12',
    name: 'Daniel Harris',
    phone: '(555) 567-8903',
    email: 'dharris@email.com',
    address: '1234 Magnolia Street, Charlotte, NC 28201',
    loanNumber: 'ML-2023-1122',
    fundedDate: '2023-02-18',
    loanType: 'VA',
    interestRate: 6.375,
    loanAmount: 425000,
    loa: 'Jennifer Adams',
    manager: 'Sarah Williams',
    status: 'Attempted',
    lastActivity: '2024-05-28',
    engagementHistory: [
      {
        id: 'e20',
        type: 'call',
        title: 'Outbound call attempt',
        timestamp: '2024-05-28 04:15 PM',
        body: 'No answer',
      },
    ],
  },
  {
    id: '13',
    name: 'Jessica Garcia',
    phone: '(555) 678-9014',
    email: 'jgarcia@email.com',
    address: '5678 Dogwood Circle, Dallas, TX 75201',
    loanNumber: 'ML-2021-6677',
    fundedDate: '2021-05-09',
    loanType: 'Conventional',
    interestRate: 5.875,
    loanAmount: 520000,
    loa: 'Robert Kim',
    manager: 'Michael Chen',
    status: 'Converted',
    lastActivity: '2024-05-26',
    engagementHistory: [
      {
        id: 'e21',
        type: 'call',
        title: 'Initial contact',
        timestamp: '2024-05-24 10:30 AM',
        body: 'Borrower interested in cash-out refinance',
      },
      {
        id: 'e22',
        type: 'call',
        title: 'Follow-up discussion',
        timestamp: '2024-05-26 01:00 PM',
        body: 'Reviewed options, borrower committed to moving forward',
      },
      {
        id: 'e23',
        type: 'status',
        title: 'Status changed to Converted',
        timestamp: '2024-05-26 01:15 PM',
      },
    ],
  },
  {
    id: '14',
    name: 'Kevin Johnson',
    phone: '(555) 789-0125',
    email: 'kjohnson@email.com',
    address: '9012 Sycamore Road, Minneapolis, MN 55401',
    loanNumber: 'ML-2022-3344',
    fundedDate: '2022-07-21',
    loanType: 'Jumbo',
    interestRate: 6.875,
    loanAmount: 780000,
    loa: 'David Park',
    manager: 'Sarah Williams',
    status: 'New',
    lastActivity: '2024-05-24',
    engagementHistory: [
      {
        id: 'e24',
        type: 'status',
        title: 'Added to engagement queue',
        timestamp: '2024-05-24 09:00 AM',
      },
    ],
  },
  {
    id: '15',
    name: 'Michelle Davis',
    phone: '(555) 890-1236',
    email: 'mdavis@email.com',
    address: '3456 Redwood Drive, San Francisco, CA 94101',
    loanNumber: 'ML-2023-9988',
    fundedDate: '2023-11-12',
    loanType: 'Conventional',
    interestRate: 7.0,
    loanAmount: 890000,
    loa: 'Jennifer Adams',
    manager: 'Michael Chen',
    status: 'Attempted',
    lastActivity: '2024-05-29',
    engagementHistory: [
      {
        id: 'e25',
        type: 'call',
        title: 'Outbound call attempt',
        timestamp: '2024-05-29 03:45 PM',
        body: 'Voicemail full, unable to leave message',
      },
    ],
  },
  {
    id: '16',
    name: 'Ryan Martinez',
    phone: '(555) 901-2347',
    email: 'rmartinez@email.com',
    address: '7890 Beech Street, Las Vegas, NV 89101',
    loanNumber: 'ML-2021-7755',
    fundedDate: '2021-03-28',
    loanType: 'FHA',
    interestRate: 5.5,
    loanAmount: 285000,
    loa: 'Robert Kim',
    manager: 'Sarah Williams',
    status: 'Connected',
    lastActivity: '2024-05-27',
    engagementHistory: [
      {
        id: 'e26',
        type: 'call',
        title: 'Successful connection',
        timestamp: '2024-05-27 11:30 AM',
        body: 'Borrower happy with current loan, not interested in changes at this time',
      },
      {
        id: 'e27',
        type: 'status',
        title: 'Status changed to Connected',
        timestamp: '2024-05-27 11:35 AM',
      },
    ],
  },
  {
    id: '17',
    name: 'Stephanie Moore',
    phone: '(555) 012-3458',
    email: 'smoore@email.com',
    address: '1357 Walnut Avenue, Philadelphia, PA 19101',
    loanNumber: 'ML-2022-5544',
    fundedDate: '2022-09-06',
    loanType: 'VA',
    interestRate: 6.25,
    loanAmount: 395000,
    loa: 'David Park',
    manager: 'Michael Chen',
    status: 'New',
    lastActivity: '2024-05-25',
    engagementHistory: [
      {
        id: 'e28',
        type: 'status',
        title: 'Added to engagement queue',
        timestamp: '2024-05-25 08:15 AM',
      },
    ],
  },
  {
    id: '18',
    name: 'Thomas Jackson',
    phone: '(555) 123-4569',
    email: 'tjackson@email.com',
    address: '2468 Cherry Lane, Houston, TX 77001',
    loanNumber: 'ML-2023-6655',
    fundedDate: '2023-05-14',
    loanType: 'Conventional',
    interestRate: 6.625,
    loanAmount: 615000,
    loa: 'Jennifer Adams',
    manager: 'Sarah Williams',
    status: 'Attempted',
    lastActivity: '2024-05-28',
    engagementHistory: [
      {
        id: 'e29',
        type: 'call',
        title: 'Outbound call attempt',
        timestamp: '2024-05-28 10:00 AM',
        body: 'Wrong number, need to update contact info',
      },
    ],
  },
  {
    id: '19',
    name: 'Nicole Thompson',
    phone: '(555) 234-5671',
    email: 'nthompson@email.com',
    address: '9753 Cypress Court, Orlando, FL 32801',
    loanNumber: 'ML-2020-8866',
    fundedDate: '2020-11-20',
    loanType: 'Jumbo',
    interestRate: 6.5,
    loanAmount: 725000,
    loa: 'Robert Kim',
    manager: 'Michael Chen',
    status: 'Connected',
    lastActivity: '2024-05-29',
    engagementHistory: [
      {
        id: 'e30',
        type: 'call',
        title: 'Successful connection',
        timestamp: '2024-05-29 09:00 AM',
        body: 'Borrower interested in investment property loan. Transferred to commercial team.',
      },
      {
        id: 'e31',
        type: 'status',
        title: 'Status changed to Connected',
        timestamp: '2024-05-29 09:10 AM',
      },
    ],
  },
  {
    id: '20',
    name: 'Brandon Clark',
    phone: '(555) 345-6782',
    email: 'bclark@email.com',
    address: '8642 Laurel Street, Detroit, MI 48201',
    loanNumber: 'ML-2022-1100',
    fundedDate: '2022-01-25',
    loanType: 'FHA',
    interestRate: 5.75,
    loanAmount: 265000,
    loa: 'David Park',
    manager: 'Sarah Williams',
    status: 'New',
    lastActivity: '2024-05-26',
    engagementHistory: [
      {
        id: 'e32',
        type: 'status',
        title: 'Added to engagement queue',
        timestamp: '2024-05-26 07:45 AM',
      },
    ],
  },
]
